import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  LockClosedIcon,
  XMarkIcon,
  CheckIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";

/* -------------------------------------------------------------------------- */
/*                               CONSTANTS & UTILS                            */
/* -------------------------------------------------------------------------- */

const ZERO_TOTALS = Object.freeze({
  ore: 0,
  minuti: 0,
  ore_ldc: 0,
  studi: 0,
  pubblicazioni: 0,
});

const SERVICE_PROFILE_OPTIONS = [
  { value: "", label: "Nessun profilo", hours: null },
  { value: "pionieraus_15", label: "Pioniere Ausiliare · 15 ore", hours: 15 },
  { value: "pionieraus_30", label: "Pioniere Ausiliare · 30 ore", hours: 30 },
  { value: "pionierereg_50", label: "Pioniere Regolare · 50 ore", hours: 50 },
  { value: "proclamatore", label: "Proclamatore", hours: null },
];

const SERVICE_PROFILE_LOOKUP = SERVICE_PROFILE_OPTIONS.reduce((acc, option) => {
  if (option.value) acc[option.value] = option;
  return acc;
}, {});

const buildProfileInfo = (key) => {
  if (!key) return null;
  const ref = SERVICE_PROFILE_OPTIONS.find((opt) => opt.value === key);
  if (!ref) return null;
  return { profile: key, label: ref.label, hours: ref.hours };
};

const normalizeTotals = (totals = ZERO_TOTALS) => {
  const ore = Number(totals.ore || 0);
  const minuti = Number(totals.minuti || 0);
  const carry = Math.floor(minuti / 60);
  return {
    ore: ore + carry,
    minuti: minuti % 60,
    ore_ldc: Number(totals.ore_ldc || 0),
    studi: Number(totals.studi || 0),
    pubblicazioni: Number(totals.pubblicazioni || 0),
  };
};

const sumTotals = (acc, value = ZERO_TOTALS) => {
  acc.ore += Number(value.ore || 0);
  acc.minuti += Number(value.minuti || 0);
  acc.ore_ldc += Number(value.ore_ldc || 0);
  acc.studi += Number(value.studi || 0);
  acc.pubblicazioni += Number(value.pubblicazioni || 0);
  return acc;
};

const formatDuration = (totals = ZERO_TOTALS) => {
  const ore = Number(totals.ore || 0);
  const minuti = Number(totals.minuti || 0);
  return `${ore}h ${String(minuti).padStart(2, "0")}m`;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, diff) =>
  new Date(date.getFullYear(), date.getMonth() + diff, 1);
const formatMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const formatMonthLabel = (date) =>
  date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
const serviceYearStartDate = (referenceDate) => {
  const monthIndex = referenceDate.getMonth(); // 0-based
  const year = referenceDate.getFullYear();
  // Service year starts in September (8)
  const startYear = monthIndex >= 8 ? year : year - 1;
  return new Date(startYear, 8, 1);
};

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const RegistroServizio = () => {
  const { activeCongregazione, user } = useAuth();
  const activeCongregazioneId = activeCongregazione?.id || null;
  const rapportoEnabled = Boolean(
    activeCongregazione?.rapporto_servizio_attivo ??
      user?.congregazione_rapporto_servizio_attivo
  );
  const isServizioSpeciale = Boolean(activeCongregazione?.servizio_speciale);

  // -- State --
  const [currentMonth, setCurrentMonth] = useState(() =>
    startOfMonth(new Date())
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Profile Management
  const [selectedProfile, setSelectedProfile] = useState("");
  const [isContinuous, setIsContinuous] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [animateBars, setAnimateBars] = useState(false);

  // Edit Manual Totals
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(ZERO_TOTALS);
  const [totalsSaving, setTotalsSaving] = useState(false);
  const [resettingTotals, setResettingTotals] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [proclamatorePartecipato, setProclamatorePartecipato] = useState(false);
  const [proclamatoreStudi, setProclamatoreStudi] = useState(0);

  const monthKey = formatMonthKey(currentMonth);
  const monthLabel = formatMonthLabel(currentMonth);

  /* -------------------------------------------------------------------------- */
  /*                               DATA FETCHING                                */
  /* -------------------------------------------------------------------------- */

  const fetchData = useCallback(
    async (options = {}) => {
      const { silent = false, targetMonthKey = monthKey } = options;
      if (!activeCongregazioneId) return;

      if (!silent) setLoading(true);
      setError(null);

      try {
        const response = await api.get("/registro-servizio/miei", {
          params: { mese: targetMonthKey },
        });
        const serverData = response.data;
        setData(serverData);

        // Selection in dropdown: use the effective profile returned by backend
        const profileKey = serverData.profilo_servizio?.profile || "";
        setSelectedProfile(profileKey);

        // Continuous Checkbox: 
        // Checked if the current profile matches the global default
        const isPastMonth = Boolean(
          serverData.isPastMonth ??
          (serverData.range?.end &&
            new Date() > new Date(`${serverData.range.end}T23:59:59Z`))
        );
        const matchesDefault =
          serverData.defaultProfile && profileKey === serverData.defaultProfile;
        setIsContinuous(Boolean(matchesDefault && !isPastMonth));

        // Prepare Edit Form with current final totals (or manual overrides if exist)
        const source = serverData.overrideTotals ?? serverData.autoTotals ?? ZERO_TOTALS;
        setEditForm(normalizeTotals(source));

      } catch (err) {
        console.error("Errore caricamento registro servizio:", err);
        setError("Impossibile recuperare i dati.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [activeCongregazioneId, monthKey]
  );

  useEffect(() => {
    if (activeCongregazioneId && rapportoEnabled !== false && !isServizioSpeciale) {
      fetchData();
    }
  }, [fetchData, activeCongregazioneId, rapportoEnabled, monthKey, isServizioSpeciale]);

  // Derived flags for current month
  const monthClosed = Boolean(data?.stato?.chiuso);
  const monthIsPast =
    data?.isPastMonth !== undefined
      ? Boolean(data.isPastMonth)
      : Boolean(
          data?.range?.end &&
            new Date() > new Date(`${data.range.end}T23:59:59Z`)
        );
  const isContinuousAllowed = !monthIsPast && !monthClosed;
  const isProclamatore = selectedProfile === "proclamatore";

  /* -------------------------------------------------------------------------- */
  /*                               HANDLERS                                     */
  /* -------------------------------------------------------------------------- */

  const handleMonthChange = (diff) => {
    setCurrentMonth((prev) => {
      const next = addMonths(prev, diff);
      // Fetch immediately for the newly selected month to keep cards in sync
      fetchData({ targetMonthKey: formatMonthKey(next) });
      return next;
    });
    setIsEditing(false); // Reset edit mode on month change
  };

  const handleProfileChange = (e) => {
    const newVal = e.target.value;
    setSelectedProfile(newVal);
  };

  const toggleContinuous = () => {
    if (!isContinuousAllowed) return;
    setIsContinuous((prev) => !prev);
  };

  const applyLocalProfile = (profileKey, isContinuousSelection) => {
    const profileInfo = buildProfileInfo(profileKey);
    setData((prev) => {
      if (!prev) return prev;
      const nextChart = Array.isArray(prev.chart)
        ? prev.chart.map((item) =>
            item.mese === monthKey ? { ...item, profilo_servizio: profileInfo } : item
          )
        : prev.chart;
      return {
        ...prev,
        profilo_servizio: profileInfo,
        defaultProfile: isContinuousSelection ? profileKey : null,
        chart: nextChart,
      };
    });
    setSelectedProfile(profileKey || "");
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    const continuativoPayload = isContinuousAllowed ? isContinuous : undefined;
    try {
      await api.post("/registro-servizio/miei/profilo", {
        mese: monthKey,
        profilo_servizio: selectedProfile,
        continuativo: continuativoPayload,
      });
      toastSuccess("Profilo aggiornato.");
      applyLocalProfile(selectedProfile, Boolean(continuativoPayload));
      await fetchData({ silent: true });
    } catch (err) {
      console.error(err);
      toastError("Errore salvataggio profilo.");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleEditChange = (field, val) => {
    const num = parseInt(val, 10);
    setEditForm((prev) => ({
      ...prev,
      [field]: isNaN(num) ? "" : Math.max(0, num),
    }));
  };

  const applyLocalManualTotals = (totals, profileKey) => {
    const normalized = normalizeTotals(totals);
    setData((prev) => {
      if (!prev) return prev;
      const nextChart = Array.isArray(prev.chart)
        ? prev.chart.map((item) =>
            item.mese === monthKey
              ? {
                  ...item,
                  totals: normalized,
                  profilo_servizio: buildProfileInfo(profileKey ?? selectedProfile),
                }
              : item
          )
        : prev.chart;
      return {
        ...prev,
        overrideTotals: normalized,
        finalTotals: normalized,
        profilo_servizio: buildProfileInfo(profileKey ?? selectedProfile),
        chart: nextChart,
      };
    });
    setEditForm(normalized);
    if (profileKey === "proclamatore" || selectedProfile === "proclamatore") {
      setProclamatoreStudi(normalized.studi || 0);
      const participated =
        (normalized.ore || 0) > 0 ||
        (normalized.minuti || 0) > 0 ||
        (normalized.ore_ldc || 0) > 0 ||
        (normalized.studi || 0) > 0;
      setProclamatorePartecipato(participated);
    }
  };

  const applyLocalAutoTotals = (totals) => {
    const normalized = normalizeTotals(totals);
    setData((prev) => {
      if (!prev) return prev;
      const nextChart = Array.isArray(prev.chart)
        ? prev.chart.map((item) =>
            item.mese === monthKey ? { ...item, totals: normalized } : item
          )
        : prev.chart;
      return {
        ...prev,
        overrideTotals: null,
        finalTotals: normalized,
        chart: nextChart,
      };
    });
    setEditForm(normalized);
  };

  const saveManualTotals = async () => {
    setTotalsSaving(true);
    const continuativoPayload = isContinuousAllowed ? isContinuous : undefined;
    try {
      const payloadTotals = isProclamatore
        ? {
            ore: finalTotals.ore,
            minuti: finalTotals.minuti,
            ore_ldc: finalTotals.ore_ldc,
            studi: proclamatorePartecipato
              ? Number(proclamatoreStudi || 0)
              : 0,
            pubblicazioni: finalTotals.pubblicazioni,
          }
        : {
            ore: Number(editForm.ore || 0),
            minuti: Number(editForm.minuti || 0),
            ore_ldc: Number(editForm.ore_ldc || 0),
            studi: Number(editForm.studi || 0),
            pubblicazioni: Number(editForm.pubblicazioni || 0),
          };
      await api.post("/registro-servizio/miei", {
        mese: monthKey,
        solo_studi: isProclamatore,
        // Preserve profile settings when saving totals
        profilo_servizio: selectedProfile,
        continuativo: continuativoPayload,
        // New totals
        ...payloadTotals,
      });
      toastSuccess("Totali manuali salvati.");
      if (!isProclamatore) applyLocalManualTotals(payloadTotals, selectedProfile);
      applyLocalProfile(selectedProfile, Boolean(continuativoPayload));
      setIsEditing(false);
      await fetchData({ silent: true });
    } catch (err) {
      console.error(err);
      toastError("Errore salvataggio totali.");
    } finally {
      setTotalsSaving(false);
    }
  };

  const resetManualTotals = async () => {
    setResettingTotals(true);
    try {
      await api.post("/registro-servizio/miei/reset", { mese: monthKey });
      toastSuccess("Totali riallineati ai rapporti di servizio.");
      applyLocalAutoTotals(autoTotals);
      setIsEditing(false);
      await fetchData({ silent: true });
    } catch (err) {
      console.error(err);
      toastError("Errore reset totali.");
    } finally {
      setResettingTotals(false);
    }
  };

  const closeMonth = async () => {
    if (!window.confirm("Chiudere definitivamente il mese?")) return;
    setClosing(true);
    try {
      await api.post("/registro-servizio/miei/chiudi", { mese: monthKey });
      toastSuccess("Mese chiuso.");
      await fetchData();
    } catch (err) {
      toastError("Impossibile chiudere il mese.");
    } finally {
      setClosing(false);
    }
  };

  const reopenMonth = async () => {
    if (!window.confirm("Riaprire il mese per consentire modifiche?")) return;
    setReopening(true);
    try {
      await api.post("/registro-servizio/miei/riapri", { mese: monthKey });
      toastSuccess("Mese riaperto.");
      await fetchData();
    } catch (err) {
      toastError("Impossibile riaprire il mese.");
    } finally {
      setReopening(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               RENDER HELPERS                               */
  /* -------------------------------------------------------------------------- */

  const autoTotals = data?.autoTotals ?? ZERO_TOTALS;
  const chartData = useMemo(() => data?.chart ?? [], [data?.chart]);
  const finalTotals = useMemo(() => {
    const currentEntry = chartData.find((item) => item.mese === monthKey);
    if (currentEntry?.totals) {
      return normalizeTotals(currentEntry.totals);
    }
    return normalizeTotals(data?.finalTotals ?? autoTotals);
  }, [autoTotals, chartData, data?.finalTotals, monthKey]);

  // Sincronizza stato Proclamatore con i totali correnti
  useEffect(() => {
    if (!isProclamatore) return;
    const totals = finalTotals;
    const participated =
      (totals.ore || 0) > 0 ||
      (totals.minuti || 0) > 0 ||
      (totals.ore_ldc || 0) > 0 ||
      (totals.studi || 0) > 0;
    setProclamatorePartecipato(participated);
    setProclamatoreStudi(totals.studi || 0);
  }, [finalTotals, isProclamatore]);

  // Annual Calculation
  const serviceYearInfo = useMemo(() => {
    const start = serviceYearStartDate(currentMonth);
    const end = addMonths(start, 11);
    return {
      start,
      end,
      label: `${start.getFullYear()}/${end.getFullYear()}`,
      rangeLabel: `${start.toLocaleDateString("it-IT", {
        month: "long",
        year: "numeric",
      })} — ${end.toLocaleDateString("it-IT", {
        month: "long",
        year: "numeric",
      })}`,
    };
  }, [currentMonth]);

  const serviceYearTotals = useMemo(() => {
    if (!chartData.length) return ZERO_TOTALS;
    const totals = { ...ZERO_TOTALS };
    // The chart data presumably covers the relevant range relative to "currentMonth".
    // Or does GET /miei return a fixed 12 months back range?
    // My backend returns "monthsBackRange(start)".
    // So if I am in Dec 2025, it returns Jan-Dec 2025.
    // Service year starts Sept 2025.
    // I need to filter chartData for months >= serviceYearInfo.start
    const startTs = serviceYearInfo.start.getTime();
    const endTs = serviceYearInfo.end.getTime();

    chartData.forEach((item) => {
      // item.mese is "YYYY-MM"
      const [y, m] = item.mese.split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      const ts = d.getTime();
      if (ts >= startTs && ts <= endTs && item.totals) {
        sumTotals(totals, item.totals);
      }
    });
    return normalizeTotals(totals);
  }, [chartData, serviceYearInfo]);
  
  // Progress Bar for current month
  const targetHours = SERVICE_PROFILE_LOOKUP[selectedProfile]?.hours || 0;
  const normalHours = (finalTotals.ore || 0) + (finalTotals.minuti || 0) / 60;
  const ldcHours = finalTotals.ore_ldc || 0;
  const totalServiceHours = normalHours + ldcHours;
  const progressPercent = targetHours
    ? Math.min(100, Math.round((totalServiceHours / targetHours) * 100))
    : 0;
  const normalPercent = targetHours
    ? Math.min(100, (normalHours / targetHours) * 100)
    : 0;
  const ldcPercent = targetHours
    ? Math.min(100 - normalPercent, (ldcHours / targetHours) * 100)
    : 0;

  // Annual progress (solo per pionieri regolari)
  const isRegularPioneer = selectedProfile === "pionierereg_50";
  const annualTargetHours = 600; // 50h x 12 mesi
  const annualNormalHours =
    (serviceYearTotals.ore || 0) + (serviceYearTotals.minuti || 0) / 60;
  const annualLdcHours = serviceYearTotals.ore_ldc || 0;
  const annualTotalHours = annualNormalHours + annualLdcHours;
  const annualProgressPercent = isRegularPioneer
    ? Math.min(100, Math.round((annualTotalHours / annualTargetHours) * 100))
    : 0;
  const annualNormalPercent = isRegularPioneer
    ? Math.min(100, (annualNormalHours / annualTargetHours) * 100)
    : 0;
  const annualLdcPercent = isRegularPioneer
    ? Math.min(
        100 - annualNormalPercent,
        (annualLdcHours / annualTargetHours) * 100
      )
    : 0;
  const annualTickPercents = useMemo(
    () => Array.from({ length: 11 }, (_v, i) => ((i + 1) * 100) / 12),
    []
  );
  const annualMarkerPercent = useMemo(() => {
    if (!serviceYearInfo?.start) return 0;
    const monthsDiff =
      (currentMonth.getFullYear() - serviceYearInfo.start.getFullYear()) * 12 +
      (currentMonth.getMonth() - serviceYearInfo.start.getMonth());
    const clampedIndex = Math.min(11, Math.max(0, monthsDiff));
    return ((clampedIndex + 1) / 12) * 100; // fine del mese corrente
  }, [currentMonth, serviceYearInfo?.start]);
  const annualExpectedHours = useMemo(() => {
    if (!isRegularPioneer) return 0;
    return (annualMarkerPercent / 100) * annualTargetHours;
  }, [annualMarkerPercent, annualTargetHours, isRegularPioneer]);
  const annualDeltaHours = annualTotalHours - annualExpectedHours;

  // Animate chart bars when data changes
  useEffect(() => {
    setAnimateBars(false);
    const t = setTimeout(() => setAnimateBars(true), 50);
    return () => clearTimeout(t);
  }, [chartData]);

  if (!activeCongregazioneId) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
         <div className="flex items-center gap-3 text-gray-700">
             <InformationCircleIcon className="h-6 w-6 text-blue-500"/>
             <p>Seleziona una congregazione attiva.</p>
         </div>
      </div>
    );
  }

  if (isServizioSpeciale) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 px-4 md:px-6 pt-4 md:pt-6">
        <div className="p-6 bg-white rounded-lg shadow border border-yellow-200">
          <div className="flex items-start gap-3 text-yellow-800">
            <InformationCircleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">
                Registro di servizio non disponibile
              </h2>
              <p className="text-sm mt-1">
                Il registro di servizio è disponibile solo per le congregazioni primarie.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (rapportoEnabled === false) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 px-4 md:px-6 pt-4 md:pt-6">
        <div className="p-6 bg-white rounded-lg shadow border border-yellow-200">
          <div className="flex items-start gap-3 text-yellow-800">
            <InformationCircleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">Registro di servizio disattivato</h2>
              <p className="text-sm mt-1">
                Il rapporto di servizio non è attivo per questa congregazione. Contatta un amministratore per abilitarlo.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
     return <div className="p-10 text-center text-gray-500">Caricamento in corso...</div>;
  }

  if (error) {
     return (
        <div className="p-4 bg-red-50 text-red-700 rounded border border-red-200">
           {error}
        </div>
     );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 md:px-6 pt-4 md:pt-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Registro di Servizio</h1>
        <p className="text-gray-600">
          Organizza il riepilogo mensile delle ore di servizio e gestisci il tuo profilo.
        </p>
      </div>

      {/* Main Control Bar */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Month Navigator */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleMonthChange(-1)}
            className="p-2 rounded-full hover:bg-gray-100 border border-gray-200"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-md">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <span className="font-semibold text-gray-900 capitalize">
              {monthLabel}
            </span>
          </div>

          <button
            onClick={() => handleMonthChange(1)}
            className="p-2 rounded-full hover:bg-gray-100 border border-gray-200"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>

          {/* Status Badge */}
          {monthClosed ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium">
              <LockClosedIcon className="h-4 w-4" /> Mese chiuso
            </span>
          ) : (
             <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
              <CheckCircleIcon className="h-4 w-4" /> Mese aperto
            </span>
          )}
        </div>

        {/* Month Actions */}
        <div className="flex gap-2">
            {monthIsPast && !monthClosed && (
                <button
                  onClick={closeMonth}
                  disabled={closing}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium disabled:opacity-50"
                >
                    {closing ? "Chiusura..." : "Chiudi mese"}
                </button>
            )}
            
            {monthClosed && (
                <button
                  onClick={reopenMonth}
                  disabled={reopening}
                  className="px-4 py-2 bg-primary-50 text-primary-700 border border-primary-200 rounded hover:bg-primary-100 text-sm font-medium disabled:opacity-50"
                >
                    {reopening ? "Riapertura..." : "Riapri mese"}
                </button>
            )}
        </div>
      </div>

      {/* Profile Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-end gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profilo Servizio
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <select
                value={selectedProfile}
                onChange={handleProfileChange}
                disabled={monthClosed || profileSaving}
                className="block w-full sm:w-64 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {SERVICE_PROFILE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isContinuous}
                  onChange={toggleContinuous}
                  disabled={!isContinuousAllowed || profileSaving}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                />
                <span className="text-sm text-gray-700">Continuativo</span>
              </label>

              <button
                onClick={saveProfile}
                disabled={monthClosed || profileSaving}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {profileSaving ? "Salvataggio..." : "Salva"}
              </button>

              {isRegularPioneer && (
                <div className="flex items-center gap-2 text-xs sm:ml-2 text-gray-700">
                  <span className="text-gray-300 select-none">|</span>
                  {annualDeltaHours < -0.01 && (
                    <span className="text-amber-700">
                      Sei indietro di {Math.abs(annualDeltaHours).toFixed(1)}h sul programma annuale
                    </span>
                  )}
                  {annualDeltaHours > 0.01 && (
                    <span className="text-emerald-700">
                      Complimenti! Sei avanti di {annualDeltaHours.toFixed(1)}h sul programma attuale
                    </span>
                  )}
                  {Math.abs(annualDeltaHours) <= 0.01 && (
                    <span className="text-gray-700">
                      Sei in pari con il programma annuale di servizio
                    </span>
                  )}
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {monthIsPast
                ? "Le modifiche ai mesi già conclusi non si propagano: il profilo vale solo per questo mese."
                : 'Seleziona "Continuativo" per applicare automaticamente questo profilo ai mesi futuri.'}
            </p>
          </div>
        </div>

        {/* Progress Bar (Visible if target > 0 and non proclamatore) */}
        {targetHours > 0 && !isProclamatore && (
          <div className="mt-6 border-t pt-6">
             <div className="flex justify-between items-center text-sm mb-2">
                 <span className="font-medium text-gray-700">
                     Avanzamento: {normalHours.toFixed(1)}h + {ldcHours.toFixed(1)}h LDC su {targetHours}h
                 </span>
                 <span className="font-bold text-primary-600">{progressPercent}%</span>
             </div>
             <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden relative">
               <div
                 className="absolute left-0 top-0 h-full bg-primary-600 transition-all duration-500"
                 style={{ width: `${normalPercent}%` }}
               ></div>
               <div
                 className="absolute top-0 h-full bg-blue-300 transition-all duration-500"
                 style={{ width: `${ldcPercent}%`, left: `${normalPercent}%` }}
               ></div>
             </div>
             <div className="flex justify-between text-xs text-gray-500 mt-2">
               <span className="flex items-center gap-1">
                 <span className="inline-block w-3 h-2 rounded-full bg-primary-600"></span> Ore standard: {normalHours.toFixed(1)}h
               </span>
               <span className="flex items-center gap-1">
                 <span className="inline-block w-3 h-2 rounded-full bg-blue-300"></span> Ore LDC: {ldcHours.toFixed(1)}h
               </span>
             </div>

             {/* Annual progress for Pioniere Regolare */}
             {isRegularPioneer && (
               <div className="mt-4 space-y-2">
                 <div className="flex justify-between items-center text-xs">
                   <span className="font-medium text-gray-700">
                     Anno di servizio: {annualTotalHours.toFixed(1)}h su {annualTargetHours}h
                   </span>
                   <span className="font-semibold text-primary-600">{annualProgressPercent}%</span>
                 </div>
                 <div className="w-full bg-blue-50 rounded-full h-2 overflow-hidden relative">
                   <div
                     className="absolute left-0 top-0 h-full bg-indigo-400 transition-all duration-500"
                     style={{ width: `${annualNormalPercent}%` }}
                   ></div>
                   <div
                     className="absolute top-0 h-full bg-sky-200 transition-all duration-500"
                     style={{ width: `${annualLdcPercent}%`, left: `${annualNormalPercent}%` }}
                   ></div>
                   {/* Tacchette ogni 50h (12 blocchi) */}
                   <div className="absolute inset-0 pointer-events-none">
                     {annualTickPercents.map((p) => (
                       <span
                         key={p}
                         className="absolute top-0 bottom-0 w-px bg-blue-200/70"
                         style={{ left: `${p}%` }}
                       ></span>
                     ))}
                   </div>
                   {/* Marker posizione mese corrente nel servizio annuale */}
                   <div
                     className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                     style={{ left: `${annualMarkerPercent}%` }}
                   >
                     <span className="block w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white shadow"></span>
                   </div>
                 </div>
                 <div className="flex justify-between text-xs text-gray-500">
                   <span className="flex items-center gap-1">
                     <span className="inline-block w-3 h-2 rounded-full bg-indigo-400"></span> Ore standard: {annualNormalHours.toFixed(1)}h
                   </span>
                   <span className="flex items-center gap-1">
                     <span className="inline-block w-3 h-2 rounded-full bg-blue-300"></span> Ore LDC: {annualLdcHours.toFixed(1)}h
                   </span>
                 </div>
               </div>
             )}
          </div>
        )}
      </div>

      {/* Cards Grid */}
      {!isProclamatore ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1: Totale Rapporti (Auto) */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 flex flex-col">
             <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                Totale Rapporti di servizio
             </h3>
             <div className="flex-1">
                 <div className="text-3xl font-bold text-gray-900 mb-4">
                     {formatDuration(autoTotals)}
                 </div>
                 <div className="space-y-2 text-sm text-gray-600">
                     <div className="flex justify-between">
                         <span>Ore LDC</span>
                         <span className="font-medium text-gray-900">{autoTotals.ore_ldc}</span>
                     </div>
                     <div className="flex justify-between">
                         <span>Studi</span>
                         <span className="font-medium text-gray-900">{autoTotals.studi}</span>
                     </div>
                     <div className="flex justify-between">
                         <span>Pubblicazioni</span>
                         <span className="font-medium text-gray-900">{autoTotals.pubblicazioni}</span>
                     </div>
                 </div>
             </div>
             <div className="mt-4 text-xs text-gray-500 italic">
                 Calcolato dai rapporti di servizio libero/altro, postazioni della congregazione e Opera Porto.
             </div>
             <Link to="/miei-turni" className="mt-2 text-xs font-semibold text-primary-700 hover:underline">Inserisci un rapporto in I miei turni</Link>
             {data?.sourceTotals && (
               <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-xs text-gray-600">
                 <div className="flex justify-between"><span>Servizio libero / altro</span><span>{formatDuration(data.sourceTotals.libero)}</span></div>
                 <div className="flex justify-between"><span>Postazioni congregazione</span><span>{formatDuration(data.sourceTotals.postazioni)}</span></div>
                 <div className="flex justify-between"><span>Opera Porto</span><span>{formatDuration(data.sourceTotals.porto)}</span></div>
               </div>
             )}
          </div>

          {/* Card 2: Totale del mese (Editable) */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm relative flex flex-col">
              <div className="flex justify-between items-start mb-4">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                      Totale del mese
                  </h3>
                  {!monthClosed && !isEditing && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={resetManualTotals}
                          disabled={resettingTotals}
                          className="px-3 py-1.5 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
                          title="Ripristina dai rapporti"
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => {
                            setEditForm(finalTotals);
                            setIsEditing(true);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold rounded bg-primary-50 text-primary-700 border border-primary-200 hover:bg-primary-100 transition-colors"
                          title="Modifica totali"
                        >
                          Modifica
                        </button>
                      </div>
                  )}
              </div>

              {isEditing ? (
                  <div className="flex-1 flex flex-col gap-3">
                      <div className="grid grid-cols-2 gap-3">
                          <div>
                              <label className="text-xs text-gray-500">Ore</label>
                              <input 
                                  type="number" 
                                  value={editForm.ore}
                                  onChange={(e) => handleEditChange("ore", e.target.value)}
                                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                              />
                          </div>
                          <div>
                              <label className="text-xs text-gray-500">Minuti</label>
                              <input 
                                  type="number" 
                                  value={editForm.minuti}
                                  onChange={(e) => handleEditChange("minuti", e.target.value)}
                                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                              />
                          </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                           <div>
                              <label className="text-xs text-gray-500">LDC</label>
                              <input 
                                  type="number" 
                                  value={editForm.ore_ldc}
                                  onChange={(e) => handleEditChange("ore_ldc", e.target.value)}
                                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                              />
                          </div>
                          <div>
                              <label className="text-xs text-gray-500">Studi</label>
                              <input 
                                  type="number" 
                                  value={editForm.studi}
                                  onChange={(e) => handleEditChange("studi", e.target.value)}
                                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                              />
                          </div>
                          <div>
                              <label className="text-xs text-gray-500">Pubb.</label>
                              <input 
                                  type="number" 
                                  value={editForm.pubblicazioni}
                                  onChange={(e) => handleEditChange("pubblicazioni", e.target.value)}
                                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                              />
                          </div>
                      </div>
                      
                      <div className="flex gap-2 mt-auto pt-4">
                          <button 
                              onClick={saveManualTotals}
                              disabled={totalsSaving}
                              className="flex-1 bg-primary-600 text-white text-xs font-bold py-2 rounded hover:bg-primary-700 flex justify-center items-center gap-1"
                          >
                              {totalsSaving ? "..." : <><CheckIcon className="h-4 w-4"/> Salva</>}
                          </button>
                          <button 
                              onClick={() => {
                                  setIsEditing(false);
                                  setEditForm(normalizeTotals(finalTotals)); // Revert
                              }}
                              className="flex-1 bg-gray-100 text-gray-700 text-xs font-bold py-2 rounded hover:bg-gray-200 flex justify-center items-center gap-1"
                          >
                              <XMarkIcon className="h-4 w-4"/> Annulla
                          </button>
                      </div>
                  </div>
              ) : (
                  <div className="flex-1">
                      <div className="text-3xl font-bold text-gray-900 mb-4">
                          {formatDuration(finalTotals)}
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex justify-between">
                              <span>Ore LDC</span>
                              <span className="font-medium text-gray-900">{finalTotals.ore_ldc}</span>
                          </div>
                          <div className="flex justify-between">
                              <span>Studi</span>
                              <span className="font-medium text-gray-900">{finalTotals.studi}</span>
                          </div>
                          <div className="flex justify-between">
                              <span>Pubblicazioni</span>
                              <span className="font-medium text-gray-900">{finalTotals.pubblicazioni}</span>
                          </div>
                      </div>
                      <div className="mt-4 text-xs text-gray-500 italic">
                           Questi sono i totali effettivi che verranno registrati per il mese.
                      </div>
                  </div>
              )}
          </div>

          {/* Card 3: Totale anno */}
          <div className="bg-primary-50 border border-primary-100 rounded-lg p-5 flex flex-col">
              <h3 className="text-sm font-medium text-primary-800 uppercase tracking-wide mb-1">
                  Totale anno di servizio {serviceYearInfo.label}
              </h3>
              <p className="text-xs text-primary-600 mb-4">{serviceYearInfo.rangeLabel}</p>
              
              <div className="flex-1">
                  <div className="text-3xl font-bold text-primary-900 mb-4">
                     {formatDuration(serviceYearTotals)}
                  </div>
                  <div className="space-y-2 text-sm text-primary-700">
                     <div className="flex justify-between">
                         <span>Ore LDC</span>
                         <span className="font-medium">{serviceYearTotals.ore_ldc}</span>
                     </div>
                     <div className="flex justify-between">
                         <span>Studi</span>
                         <span className="font-medium">{serviceYearTotals.studi}</span>
                     </div>
                     <div className="flex justify-between">
                         <span>Pubblicazioni</span>
                         <span className="font-medium">{serviceYearTotals.pubblicazioni}</span>
                     </div>
                 </div>
              </div>
          </div>

        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
            Proclamatore
          </h3>
          <p className="mb-4 text-sm text-gray-700">Totale del mese: <strong>{formatDuration(finalTotals)}</strong>{data?.overrideTotals ? ' (manuale)' : ' (automatico)'}</p>
          <Link to="/miei-turni" className="mb-4 inline-block text-xs font-semibold text-primary-700 hover:underline">Inserisci un rapporto in I miei turni</Link>
          {data?.sourceTotals && <div className="mb-4 grid gap-2 text-xs text-gray-600 sm:grid-cols-3">
            <span>Servizio libero / altro: {formatDuration(data.sourceTotals.libero)}</span>
            <span>Postazioni: {formatDuration(data.sourceTotals.postazioni)}</span>
            <span>Opera Porto: {formatDuration(data.sourceTotals.porto)}</span>
          </div>}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">Ho partecipato</p>
                <p className="text-xs text-gray-500">Seleziona se hai partecipato nel mese.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setProclamatorePartecipato(true)}
                  disabled={monthClosed}
                  className={`px-3 py-1.5 rounded text-sm font-semibold border ${
                    proclamatorePartecipato
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Sì
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProclamatorePartecipato(false);
                    setProclamatoreStudi(0);
                  }}
                  disabled={monthClosed}
                  className={`px-3 py-1.5 rounded text-sm font-semibold border ${
                    !proclamatorePartecipato
                      ? "bg-gray-200 text-gray-800 border-gray-300"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  No
                </button>
              </div>
            </div>
            {proclamatorePartecipato && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Numero studi biblici
                </label>
                <input
                  type="number"
                  min="0"
                  value={proclamatoreStudi}
                  onChange={(e) =>
                    setProclamatoreStudi(
                      Math.max(0, parseInt(e.target.value || "0", 10))
                    )
                  }
                  disabled={monthClosed}
                  className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={saveManualTotals}
                disabled={totalsSaving || monthClosed}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {totalsSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chart Section */}
      <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">Andamento annuale</h3>
          <div className="h-64 grid grid-cols-12 gap-2 items-end">
            {chartData.map((item) => {
              const isCurrent = item.mese === monthKey;
              const itemHours =
                (item.totals?.ore || 0) + (item.totals?.minuti || 0) / 60;
              const itemLdc = item.totals?.ore_ldc || 0;
              const maxH = Math.max(
                1,
                ...chartData.map((c) => {
                  const t = c.totals || ZERO_TOTALS;
                  return (t.ore || 0) + (t.minuti || 0) / 60 + (t.ore_ldc || 0);
                }),
                80
              );
              const itemTotal = itemHours + itemLdc;
              const normalPct = Math.min(
                100,
                (itemHours / maxH) * 100
              );
              const ldcPct = Math.min(100 - normalPct, (itemLdc / maxH) * 100);

              const profileKey = item.profilo_servizio?.profile;
              let barColorClass = itemTotal > 0 ? "bg-gray-500" : "bg-gray-300"; // default proclamatore
              if (profileKey === "pionierereg_50") barColorClass = "bg-indigo-500";
              else if (profileKey === "pionieraus_30") barColorClass = "bg-orange-500";
              else if (profileKey === "pionieraus_15") barColorClass = "bg-amber-400";

              return (
                <div
                  key={item.mese}
                  className="group flex flex-col items-center gap-2 relative h-full"
                >
                  {/* Tooltip on hover */}
                  <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 pointer-events-none transition-opacity">
                    <div className="font-bold">{item.label}</div>
                    <div>{formatDuration(item.totals)}</div>
                    {item.profilo_servizio && (
                      <div className="text-gray-300 mt-1">
                        {item.profilo_servizio.label.split("·")[0]}
                      </div>
                    )}
                  </div>

                  <div className="w-full bg-blue-50 rounded-t-lg h-40 relative overflow-hidden">
                    <div
                      className={`absolute left-0 bottom-0 w-full ${barColorClass} transition-[height] duration-1000 ease-out`}
                      style={{ height: `${animateBars ? normalPct : 0}%` }}
                    ></div>
                    <div
                      className="absolute left-0 bottom-0 w-full bg-blue-200 transition-[height] duration-1000 ease-out"
                      style={{
                        height: `${animateBars ? ldcPct : 0}%`,
                        transform: `translateY(-${animateBars ? normalPct : 0}%)`,
                      }}
                    ></div>
                    {isCurrent && (
                      <div className="absolute inset-0 ring-2 ring-offset-2 ring-primary-500 pointer-events-none"></div>
                    )}
                  </div>
                  <span
                    className={`text-xs ${
                      isCurrent ? "font-bold text-primary-700" : "text-gray-500"
                    } truncate w-full text-center`}
                  >
                    {new Date(item.mese + "-01").toLocaleDateString("it-IT", {
                      month: "short",
                    })}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-6 justify-center flex-wrap text-xs text-gray-500">
             <div className="flex items-center gap-1">
                 <span className="w-3 h-3 rounded-full bg-indigo-500"></span> Pioniere Regolare
             </div>
             <div className="flex items-center gap-1">
                 <span className="w-3 h-3 rounded-full bg-orange-500"></span> Pioniere Ausiliario (30)
             </div>
             <div className="flex items-center gap-1">
                 <span className="w-3 h-3 rounded-full bg-amber-400"></span> Pioniere Ausiliario (15)
             </div>
            <div className="flex items-center gap-1">
                 <span className="w-3 h-3 rounded-full bg-gray-600"></span> Proclamatore
             </div>
          </div>
      </div>
    </div>
  );
};

export default RegistroServizio;
